import React from 'react';
import { motion } from 'motion/react';
import { Mail } from 'lucide-react';
import { usePageTitle } from '../lib/usePageTitle';
import './public-legal-contact.css';

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-[1.75rem] md:text-[2rem] font-display font-bold text-brand-primary tracking-tight mt-14 sm:mt-16 mb-5">
    {children}
  </h2>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-lg font-black uppercase tracking-[0.18em] text-brand-accent mt-10 mb-4">
    {children}
  </h3>
);

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-base md:text-lg text-brand-muted font-medium leading-relaxed mb-4">
    {children}
  </p>
);

export const PrivacyPage: React.FC = () => {
  usePageTitle('QuantumHire - Privacy');
  return (
    <div className="qh-privacy min-h-screen bg-white pt-12 sm:pt-16 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-display font-bold text-brand-primary mb-6 tracking-tight">
            Privacy Notice
          </h1>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-muted">
            Last updated: 2026-05-04
          </p>
        </motion.div>

        <Body>
          QuantumHire is committed to protecting and respecting your privacy. Accordingly, we have
          developed this Privacy Notice for you to understand how we collect, use, communicate,
          disclose, and make use of personal data through our website.
        </Body>
        <Body>
          The terms &ldquo;we,&rdquo; &ldquo;us,&rdquo; and &ldquo;our&rdquo; refer to QuantumHire
          and its affiliates (the &ldquo;Company&rdquo;).
        </Body>
        <Body>
          The term &ldquo;Site&rdquo; refers to{' '}
          <span className="text-brand-primary font-bold">www.quantumhire.ai</span>.
        </Body>
        <Body>
          The terms &ldquo;user,&rdquo; &ldquo;you,&rdquo; and &ldquo;your&rdquo; refer to Site
          visitors, candidates, and any other users of the Site.
        </Body>
        <Body>
          Use of the Site, including all information presented herein and all online services
          provided by the Company, is subject to this Privacy Notice.
        </Body>

        <SectionHeading>Who are we?</SectionHeading>
        <Body>
          QuantumHire is a technology consulting firm that provides Digital, Cloud, Data,
          Analytics, AI, and Business Services to companies of all sizes around the world.
          QuantumHire offers recruitment services for contract or temporary workers.
        </Body>

        <SectionHeading>Who does this Privacy Notice protect?</SectionHeading>
        <Body>
          This Privacy Notice applies to all Site visitors, candidates, and all other users of the
          Site. By using the Site or any of its services, you agree to this Privacy Notice, without
          modification, and acknowledge reading it.
        </Body>

        <SectionHeading>What information do we collect?</SectionHeading>
        <Body>
          When you use or register with the Site, some of the data that we collect and receive from
          you is personal data, which means that it is information that could personally identify
          you (&ldquo;Personal Data&rdquo;).
        </Body>
        <Body>
          The categories of Personal Data we may request include your name and contact information
          (email address, address, and telephone number). Depending on the nature of your inquiry
          or activities on the Site, we may also ask for your job search criteria and preferences,
          your employment experience, educational history, your skills in various areas, reference
          information, background information, and other information contained in your resume, and
          any login ID and password created by you. It is also possible to upload your resume/CV,
          cover letters, and any other attachments you may wish us to consider. The Site also
          utilizes functional cookies to track your online activities, including, but not limited
          to, registration, submissions, information requests, and for analytic purposes.
        </Body>
        <Body>
          The Company currently does not recognize and respond to Do Not Track Signals. If we do so
          in the future, we will revise this Privacy Notice to describe how this is done.
        </Body>

        <SectionHeading>Why do we process your Personal Data?</SectionHeading>
        <Body>
          We use your Personal Data for our business and activities and in our efforts to expand
          and improve our business. Generally, we use Personal Data and other data collected
          through the Site to register you with the Site, to provide you with information regarding
          employment opportunities and career-related information, and to otherwise provide you
          with requested information or services.
        </Body>
        <Body>
          We may also from time to time use your Personal Data to send you automated email messages
          regarding our services, including employment information. You have the right to ask us
          not to process your Personal Data for marketing purposes and can opt out of receiving
          such email messages by sending an email with this request to{' '}
          <a
            href="mailto:privacy@quantumhire.ai"
            className="text-brand-accent font-bold hover:brightness-110"
          >
            privacy@quantumhire.ai
          </a>{' '}
          from each e-mail account that you would like to opt out from.
        </Body>

        <SectionHeading>With whom do we share your Personal Data?</SectionHeading>
        <Body>
          The Company may disclose Personal Data to third parties. The disclosure to third parties
          would occur in the following circumstances:
        </Body>
        <Body>
          We will disclose information to our clients when necessary and appropriate in connection
          with the provision of staffing and recruitment services.
        </Body>
        <Body>
          We may disclose information to companies and individuals we employ to perform business
          functions and services on our behalf.
        </Body>
        <Body>
          We may disclose information to the extent necessary or appropriate to government
          agencies, advisors, and other third parties in order to comply with applicable laws, the
          service of legal process, or if we reasonably believe that such action is necessary to:
          (a) comply with the law requiring such disclosure; (b) protect the rights or property of
          the Company; (c) prevent a crime or protect national security; or (d) protect the
          personal safety of the users or the public.
        </Body>

        <SectionHeading>What rights do you have?</SectionHeading>
        <Body>
          We respect your right to review and request changes to your Personal Data. The amount of
          Personal Data you are required to provide when requesting our services is consistent with
          the Company&apos;s interests in providing our services to you. The Company complies with
          applicable data protection laws and regulations.
        </Body>
        <Body>
          We process Personal Data about you for the purposes set out in this Privacy Notice,
          including but not limited to providing recruitment, placement, employment, or outsourcing
          services; operating our business; complying with our legal and contractual obligations;
          and fulfilling other legitimate interests of the Company.
        </Body>

        <SectionHeading>Changes to this Privacy Notice</SectionHeading>
        <Body>
          We reserve the right to update this Privacy Notice periodically to reflect changes in our
          privacy practices and legal updates. Any changes we may make to our Privacy Notice in the
          future will be posted on this page and are immediately binding without any requirement
          for notice.
        </Body>

        <SectionHeading>How to contact us?</SectionHeading>
        <Body>
          If you have any questions or comments about this Privacy Notice, or if you would like to
          exercise your rights, please send an email to{' '}
          <a
            href="mailto:privacy@quantumhire.ai"
            className="text-brand-accent font-bold hover:brightness-110"
          >
            privacy@quantumhire.ai
          </a>
          .
        </Body>

        <div className="border-t border-gray-100 mt-16 pt-12">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-brand-primary tracking-tight mb-8">
            Cookie Policy
          </h2>

          <Body>
            Our Web sites use cookies and technologies which are similar to cookies. A cookie is a
            small piece of information that is sent to your browser and stored in your
            device&apos;s memory.
          </Body>
          <Body>
            We use cookies to deliver services requested by you, as well as to improve the quality
            of our online services by storing user preferences and monitoring user trends. We use
            several types of cookies:
          </Body>
          <Body>
            Authentication and authorization cookies allow access to secure areas and specific
            functionality of the online services. These are strictly necessary for the proper
            operation of our Web site and the services that we provide to you.
          </Body>
          <Body>
            Personalization cookies allow our Web site to remember the choices you make and provide
            personalized features. They may also be used to provide services in the way you have
            specified.
          </Body>
          <Body>
            Tracking cookies collects information about how you use our website, what pages you
            visit on our Web site, and how often. We use this information to improve our website
            and the services we offer.
          </Body>
          <Body>
            You can control how your browser handles cookies received from our Web site. You can
            choose to refuse all cookies, be prompted before a cookie is saved, or only accept
            cookies from certain Web sites that you designate. Information on deleting or
            controlling cookies is available at{' '}
            <a
              href="https://www.allaboutcookies.org"
              target="_blank"
              rel="noreferrer"
              className="text-brand-accent font-bold hover:brightness-110"
            >
              www.allaboutcookies.org
            </a>
            . By refusing to accept cookies from us, you may not be able to use some of the
            features and functionality available on our Web site.
          </Body>
          <Body>
            QuantumHire also uses analytics tools to understand our site visitors better and
            deliver relevant ads to you. You can also find more information about controlling
            third-party advertising networks from the Digital Advertising Alliance:{' '}
            <a
              href="https://youradchoices.com/control"
              target="_blank"
              rel="noreferrer"
              className="text-brand-accent font-bold hover:brightness-110"
            >
              youradchoices.com/control
            </a>
            . Tools such as Ghostery (
            <a
              href="https://www.ghostery.com"
              target="_blank"
              rel="noreferrer"
              className="text-brand-accent font-bold hover:brightness-110"
            >
              www.ghostery.com
            </a>
            ) can also assist you in controlling a variety of online trackers. Google offers the
            following tools:{' '}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noreferrer"
              className="text-brand-accent font-bold hover:brightness-110"
            >
              tools.google.com/dlpage/gaoptout
            </a>{' '}
            and{' '}
            <a
              href="https://adssettings.google.com"
              target="_blank"
              rel="noreferrer"
              className="text-brand-accent font-bold hover:brightness-110"
            >
              adssettings.google.com
            </a>
            .
          </Body>
          <Body>
            If you have any questions or concerns about the cookies on our site, please contact us
            at{' '}
            <a
              href="mailto:privacy@quantumhire.ai"
              className="text-brand-accent font-bold hover:brightness-110"
            >
              privacy@quantumhire.ai
            </a>
            .
          </Body>
          <Body>The following lists include the cookies you may encounter throughout our site:</Body>

          <SubHeading>Strictly Necessary Cookies</SubHeading>
          <Body>
            These cookies are necessary for the Web site to function and cannot be switched off in
            our systems. They are usually only set in response to actions made by you which amount
            to a request for services, such as setting your privacy preferences, logging in or
            filling in forms. You can set your browser to block or alert you about these cookies,
            but some parts of the site will not then work. These cookies do not store any
            personally identifiable information.
          </Body>

          <SubHeading>Functional Cookies</SubHeading>
          <Body>
            These cookies enable the Web site to provide enhanced functionality and personalization.
            They may be set by us or by third-party providers whose services we have added to our
            pages. If you do not allow these cookies then some or all of these services may not
            function properly.
          </Body>

          <SubHeading>Performance Cookies</SubHeading>
          <Body>
            These cookies allow us to count visits and traffic sources so we can measure and improve
            the performance of our site. They help us to know which pages are the most and least
            popular and see how visitors move around the site. All information these cookies
            collect is aggregated and therefore anonymous. If you do not allow these cookies we
            will not know when you have visited our site, and will not be able to monitor its
            performance.
          </Body>

          <SubHeading>Targeting Cookies</SubHeading>
          <Body>
            These cookies may be set through our site by our advertising partners. They may be used
            by those companies to build a profile of your interests and show you relevant adverts
            on other sites. They do not store directly personal information, but are based on
            uniquely identifying your browser and device. If you do not allow these cookies, you
            will experience less targeted advertising.
          </Body>
        </div>

        <section className="mt-16 pt-10 border-t border-gray-100 text-center">
          <p className="text-lg font-bold text-brand-primary mb-3">
            Have privacy-related queries?
          </p>
          <a
            href="mailto:privacy@quantumhire.ai"
            className="inline-flex items-center justify-center gap-2 text-brand-accent font-black uppercase tracking-widest text-sm hover:brightness-110 transition-all"
          >
            <Mail className="w-4 h-4" /> privacy@quantumhire.ai
          </a>
        </section>
      </div>
    </div>
  );
};
